import React, { useState, useEffect, forwardRef, useId } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Typography } from '../typography';
import { Spacer } from '../spacer';
import { NestedComponentRenderer } from '../componentRenderer/NestedComponentRenderer';

// Interface

export interface ExpanderPanelProps {
  title: string;
  as?: 'div' | 'section' | 'li';
  open?: boolean;
  disabled?: boolean;
  ref?: any;
  content?: any;
  components?: any; // TODO
  expanded: boolean;
}

export interface ButtonProps {
  url?: string;
  label?: string;
  onClick?: any;
}

interface StyledExpanderPanelProps {
  isOpen: boolean;
}

// Styles
const StyledExpanderContent = styled('div', { target: 'content' })`
  ${({ theme: { space, media } }) => css`
    margin-top: ${space.medium};
  `};
`;

const StyledHeadingIcon = styled('span', { target: 'icon' })`
  ${({ theme: { colors, space } }) => css`
    display: block;
    position: relative;
    width: 20px;
    height: 20px;
    transition: all 0.25s ease-in-out;
    margin-left: ${space.large};
    flex-shrink: 0;
    &::before {
      content: '';
      width: 100%;
      height: 2px;
      border-radius: 4px;
      background: ${colors.brandOne100};
      display: block;
      position: absolute;
      left: 0;
      top: 50%;
      opacity: 1;
      transform: translateY(-50%);
      transition: opacity 0.3s ease-in-out;
    }
    &::after {
      content: '';
      width: 2px;
      border-radius: 4px;
      height: 100%;
      background: ${colors.brandOne100};
      display: block;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      transition: all 0.3s ease-in-out;
    }
  `};
`;

const StyledExpanderPanel = styled.div<StyledExpanderPanelProps>`
  ${({ theme: { space, colors, borderRadii }, isOpen }) => css`
    padding: ${space.medium};
    border-radius: 8px;
    border: 2px solid ${colors.brandTwo10};

    &:hover {
      border: 2px solid ${colors.brandOne100};
    }
    /* Animate content opening */
    ${StyledExpanderContent} {
      display: ${isOpen ? 'block' : 'none'};
    }

    /* Animate Icon opening */
    ${StyledHeadingIcon} {
      transform: rotate(${isOpen ? '45deg' : '0'});
      &::before {
        opacity: ${isOpen ? '0' : '1'};
      }
      &::after {
        ${isOpen &&
        css`
          transform: rotate(45deg);
        `}
      }
    }
  `};
`;

const StyledHeading = styled.button`
  ${({ theme: { space } }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    font-weight: 700;
  `};
`;

// JSX

export const ExpanderPanel = forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<ExpanderPanelProps>
>(({ title, as, open, disabled, components, expanded, ...rest }, ref) => {
  const [isOpen, setIsOpen] = useState(open || expanded);
  const id = title.replace(/\s+/g, '-').toLowerCase();

  const panelId = `expander-panel-${useId()}`;
  const buttonId = `expander-button-${useId()}`;

  useEffect(() => {
    setIsOpen(open || expanded);
  }, [open, expanded]);

  const handleClick = () => {
    if (disabled) {
      return;
    }
    setIsOpen(!isOpen);
  };

  return (
    <StyledExpanderPanel {...rest} as={as} isOpen={isOpen} ref={ref}>
      <Typography component="h2" variant="headingMediumBold">
        {expanded ? (
          <span>{title} YO</span>
        ) : (
          <StyledHeading
            type="button"
            onClick={handleClick}
            id={buttonId}
            aria-controls={panelId}
            aria-expanded={isOpen}
          >
            <span>{title}</span>
            <StyledHeadingIcon />
          </StyledHeading>
        )}
      </Typography>
      <StyledExpanderContent
        id={panelId}
        tabIndex={isOpen ? 0 : -1}
        aria-labelledby={buttonId}
        aria-hidden={!isOpen}
      >
        <Spacer>{components}</Spacer>
      </StyledExpanderContent>
    </StyledExpanderPanel>
  );
});

ExpanderPanel.displayName = 'ExpanderPanel';
export default ExpanderPanel;
